import type { TranslationFunction } from '@/app/components/translations/Translation';
import type { SearchInfoData, SearchInfoDetailData } from '@/app/search/types';
import { DESTINATION_TYPE } from '@/app/search/types';
import { SearchModes } from '@/domainLogic/index.ts';
import { isGeoCity, isGeoTag } from '@/app/components/search/Utilities.ts';

// Flight-only has no page of its own here; it keeps the dynamic-package wording, which is the one
// that names a flight.
const resultsTitleKey: { [key in SearchModes]: string } = {
  [SearchModes.HO]: 'vacation.common.page_title_ho_results',
  [SearchModes.DP]: 'vacation.common.page_title_dp_results',
  [SearchModes.TH]: 'vacation.common.page_title_th_results',
  [SearchModes.FF]: 'vacation.common.page_title_dp_results',
};

const detailTitleKey: { [key in SearchModes]: string } = {
  [SearchModes.HO]: 'vacation.common.page_title_ho_detail',
  [SearchModes.DP]: 'vacation.common.page_title_dp_detail',
  [SearchModes.TH]: 'vacation.common.page_title_th_detail',
  [SearchModes.FF]: 'vacation.common.page_title_dp_detail',
};

type CreateGeneralPageTitleProps = {
  searchInfoData: SearchInfoData;
  searchInfoDetailData: SearchInfoDetailData;
  t: TranslationFunction;
  defaultTitle?: string;
};
export const createGeneralPageTitle = ({
  searchInfoData,
  searchInfoDetailData,
  t,
  defaultTitle = '',
}: CreateGeneralPageTitleProps): string | undefined => {
  const destinationType = searchInfoDetailData?.destination?.destinationType;
  const destinationId = searchInfoDetailData?.destination?.id;
  let destinationName;

  if (
    searchInfoData.searchMode === SearchModes.HO &&
    destinationType === DESTINATION_TYPE.GEO &&
    (isGeoCity(destinationId) || isGeoTag(destinationId))
  ) {
    destinationName = searchInfoDetailData?.destination?.description;
  } else {
    destinationName = searchInfoDetailData?.destination?.cityName;
  }

  if (!destinationName) return defaultTitle;

  return t(resultsTitleKey[searchInfoData.searchMode], { destination: destinationName });
};

type CreateDetailPageTitleProps = {
  hotelName: string | undefined;
  searchInfoData: SearchInfoData;
  searchInfoDetailData: SearchInfoDetailData;
  t: TranslationFunction;
};
export const createDetailPageTitle = ({
  hotelName,
  searchInfoData,
  searchInfoDetailData,
  t,
}: CreateDetailPageTitleProps): string | undefined => {
  const destinationName = searchInfoDetailData?.destination?.cityName || searchInfoDetailData?.destination?.description;

  if (hotelName && destinationName) {
    return t(detailTitleKey[searchInfoData.searchMode], { hotel_name: hotelName, destination: destinationName });
  }

  return hotelName ?? undefined;
};

export const setPageTitle = (pageTitle: string | undefined) => {
  if (pageTitle && typeof document !== 'undefined') {
    document.title = pageTitle;
  }
};
